import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { 
  Building2, 
  Search, 
  Plus, 
  Shield, 
  Users, 
  CheckCircle, 
  Clock, 
  X, 
  Eye, 
  Settings,
  Mail,
  Globe,
  Award,
  AlertCircle,
  Filter,
  ArrowRight,
  Link
} from 'lucide-react';

interface OrganizationConnection {
  id: string;
  name: string;
  type: string;
  industry: string;
  status: 'connected' | 'pending_approval' | 'pending_request' | 'rejected';
  trustLevel: 'full' | 'partial' | 'verification_only';
  connectionDate: string;
  credentialsShared: number;
  verificationsPerformed: number;
  description: string;
  website?: string;
  email?: string;
  logo?: string;
  lastActivity: string;
}

export default function OrganizationConnections() {
  const { user, tenant } = useAuth();
  const [activeTab, setActiveTab] = useState('connected');
  const [searchTerm, setSearchTerm] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  
  // Form state for the connect organization modal
  const [orgName, setOrgName] = useState('');
  const [orgEmail, setOrgEmail] = useState('');
  const [orgWebsite, setOrgWebsite] = useState('');
  const [orgType, setOrgType] = useState('');
  const [orgIndustry, setOrgIndustry] = useState('');
  const [trustLevel, setTrustLevel] = useState('');
  const [connectionMessage, setConnectionMessage] = useState('');

  // For now, use empty array until real API is implemented
  const mockConnections: OrganizationConnection[] = [];

  const filteredConnections = mockConnections.filter(conn => {
    const matchesSearch = conn.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         conn.industry.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         conn.type.toLowerCase().includes(searchTerm.toLowerCase());
    
    switch (activeTab) {
      case 'connected':
        return matchesSearch && conn.status === 'connected';
      case 'pending':
        return matchesSearch && (conn.status === 'pending_approval' || conn.status === 'pending_request');
      case 'all':
        return matchesSearch;
      default:
        return matchesSearch;
    }
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'connected':
        return <Badge className="bg-verified-green text-white"><CheckCircle className="h-3 w-3 mr-1" />Connected</Badge>;
      case 'pending_approval':
        return <Badge className="bg-amber-500 text-white"><Clock className="h-3 w-3 mr-1" />Pending Approval</Badge>;
      case 'pending_request':
        return <Badge className="bg-blue-500 text-white"><Clock className="h-3 w-3 mr-1" />Request Sent</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500 text-white"><X className="h-3 w-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="outline">Unknown</Badge>;
    }
  };

  const getTrustLevelBadge = (level: string) => {
    switch (level) {
      case 'full':
        return <Badge variant="outline" className="border-verified-green text-verified-green">Full Trust</Badge>;
      case 'partial':
        return <Badge variant="outline" className="border-amber-500 text-amber-600">Partial Trust</Badge>;
      case 'verification_only':
        return <Badge variant="outline" className="border-electric-steel text-electric-steel">Verification Only</Badge>;
      default:
        return <Badge variant="outline">Not Set</Badge>;
    }
  };

  const handleConnectOrganization = () => {
    // In a real implementation, this would make an API call
    console.log('Connecting to organization:', {
      orgName,
      orgEmail,
      orgWebsite,
      orgType,
      orgIndustry,
      trustLevel,
      connectionMessage
    });
    
    // Reset form and close modal
    setOrgName('');
    setOrgEmail('');
    setOrgWebsite('');
    setOrgType('');
    setOrgIndustry('');
    setTrustLevel('');
    setConnectionMessage('');
    setShowInviteModal(false);
    
    // Show success message (in real app, this would be a toast notification)
    alert('Connection request sent successfully!');
  };

  const connectedCount = mockConnections.filter(c => c.status === 'connected').length;
  const pendingCount = mockConnections.filter(c => c.status === 'pending_approval' || c.status === 'pending_request').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-display font-bold text-charcoal-ink">Organization Connections</h2>
          <p className="text-warm-slate mt-1">
            Manage your trusted organizational relationships and credential sharing partnerships
          </p>
        </div>
        <Button
          onClick={() => setShowInviteModal(true)}
          className="credential-capital-button-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Connect Organization
        </Button>
      </div>

      {/* Connect Organization Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Link className="h-5 w-5 mr-2 text-electric-steel" />
              Connect to Organization
            </DialogTitle>
            <DialogDescription>
              Send a connection request to establish a trusted relationship for credential sharing and verification.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="org-name">Organization Name *</Label>
                <Input
                  id="org-name"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="e.g., Stanford University"
                  className="credential-capital-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-email">Contact Email *</Label>
                <Input
                  id="org-email"
                  type="email"
                  value={orgEmail}
                  onChange={(e) => setOrgEmail(e.target.value)}
                  placeholder="admin@organization.com"
                  className="credential-capital-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="org-type">Organization Type</Label>
                <Select value={orgType} onValueChange={setOrgType}>
                  <SelectTrigger className="credential-capital-input">
                    <SelectValue placeholder="Select organization type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="educational">Educational Institution</SelectItem>
                    <SelectItem value="corporate">Corporation</SelectItem>
                    <SelectItem value="government">Government Agency</SelectItem>
                    <SelectItem value="nonprofit">Non-Profit Organization</SelectItem>
                    <SelectItem value="professional">Professional Association</SelectItem>
                    <SelectItem value="regulatory">Regulatory Body</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="org-industry">Industry</Label>
                <Select value={orgIndustry} onValueChange={setOrgIndustry}>
                  <SelectTrigger className="credential-capital-input">
                    <SelectValue placeholder="Select industry" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="education">Education</SelectItem>
                    <SelectItem value="technology">Technology</SelectItem>
                    <SelectItem value="healthcare">Healthcare</SelectItem>
                    <SelectItem value="finance">Finance</SelectItem>
                    <SelectItem value="manufacturing">Manufacturing</SelectItem>
                    <SelectItem value="government">Government</SelectItem>
                    <SelectItem value="real-estate">Real Estate</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="org-website">Website (Optional)</Label>
              <Input
                id="org-website"
                type="url"
                value={orgWebsite}
                onChange={(e) => setOrgWebsite(e.target.value)}
                placeholder="https://organization.com"
                className="credential-capital-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="trust-level">Proposed Trust Level</Label>
              <Select value={trustLevel} onValueChange={setTrustLevel}>
                <SelectTrigger className="credential-capital-input">
                  <SelectValue placeholder="Select trust level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="verification_only">
                    <div className="flex flex-col">
                      <span>Verification Only</span>
                      <span className="text-xs text-warm-slate">Can verify credentials but not access sensitive data</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="partial">
                    <div className="flex flex-col">
                      <span>Partial Trust</span>
                      <span className="text-xs text-warm-slate">Can access some credential fields and metadata</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="full">
                    <div className="flex flex-col">
                      <span>Full Trust</span>
                      <span className="text-xs text-warm-slate">Can access all credential data and perform all operations</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="connection-message">Connection Message (Optional)</Label>
              <Textarea
                id="connection-message"
                value={connectionMessage}
                onChange={(e) => setConnectionMessage(e.target.value)}
                placeholder="Introduce your organization and explain the purpose of this connection..."
                className="credential-capital-input min-h-[100px]"
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900 text-sm">Connection Process</h4>
                  <p className="text-blue-700 text-sm mt-1">
                    A connection request will be sent to the organization's admin. They can approve, modify the trust level, or decline your request.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowInviteModal(false)}
              className="credential-capital-button-secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConnectOrganization}
              disabled={!orgName || !orgEmail}
              className="credential-capital-button-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              Send Connection Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="credential-capital-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-warm-slate">Connected Orgs</p>
                <p className="text-2xl font-semibold text-verified-green">{connectedCount}</p>
              </div>
              <div className="p-3 rounded-full bg-verified-green/10">
                <CheckCircle className="h-6 w-6 text-verified-green" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="credential-capital-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-warm-slate">Pending Requests</p>
                <p className="text-2xl font-semibold text-amber-500">{pendingCount}</p>
              </div>
              <div className="p-3 rounded-full bg-amber-500/10">
                <Clock className="h-6 w-6 text-amber-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="credential-capital-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-warm-slate">Credentials Shared</p>
                <p className="text-2xl font-semibold text-charcoal-ink">
                  {mockConnections.reduce((sum, c) => sum + c.credentialsShared, 0)}
                </p>
              </div>
              <div className="p-3 rounded-full bg-electric-steel/10">
                <Award className="h-6 w-6 text-electric-steel" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="credential-capital-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-warm-slate">Verifications</p>
                <p className="text-2xl font-semibold text-charcoal-ink">
                  {mockConnections.reduce((sum, c) => sum + c.verificationsPerformed, 0)}
                </p>
              </div>
              <div className="p-3 rounded-full bg-electric-steel/10">
                <Shield className="h-6 w-6 text-electric-steel" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Card className="credential-capital-card">
        <CardContent className="pt-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="flex items-center justify-between mb-6">
              <TabsList className="grid w-auto grid-cols-3">
                <TabsTrigger value="connected">Connected ({connectedCount})</TabsTrigger>
                <TabsTrigger value="pending">Pending ({pendingCount})</TabsTrigger>
                <TabsTrigger value="all">All ({mockConnections.length})</TabsTrigger>
              </TabsList>

              <div className="flex items-center space-x-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-warm-slate" />
                  <Input
                    placeholder="Search organizations..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 w-64"
                  />
                </div>
                <Button variant="outline" size="sm">
                  <Filter className="h-4 w-4 mr-2" />
                  Filter
                </Button>
              </div>
            </div>

            <TabsContent value={activeTab} className="space-y-4">
              {filteredConnections.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-charcoal-ink mb-2">
                    {searchTerm ? 'No organizations found' : 'No connections yet'}
                  </h3>
                  <p className="text-warm-slate mb-6">
                    {searchTerm 
                      ? 'Try adjusting your search terms or check other tabs.'
                      : 'Start connecting with other organizations to share and verify credentials.'
                    }
                  </p>
                  <Button
                    onClick={() => setShowInviteModal(true)}
                    className="credential-capital-button-primary"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Connect Organization
                  </Button>
                </div>
              ) : (
                <div className="grid gap-4">
                  {filteredConnections.map((connection) => (
                    <Card key={connection.id} className="border hover:shadow-lg transition-shadow">
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start space-x-4 flex-1">
                            <div className="p-3 rounded-full bg-electric-steel/10">
                              <Building2 className="h-6 w-6 text-electric-steel" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-3 mb-2">
                                <h3 className="font-semibold text-charcoal-ink">{connection.name}</h3>
                                {getStatusBadge(connection.status)}
                                {getTrustLevelBadge(connection.trustLevel)}
                              </div>
                              
                              <div className="space-y-2">
                                <div className="flex items-center space-x-4 text-sm text-warm-slate">
                                  <span className="flex items-center">
                                    <Users className="h-3 w-3 mr-1" />
                                    {connection.type}
                                  </span>
                                  <span className="flex items-center">
                                    <Building2 className="h-3 w-3 mr-1" />
                                    {connection.industry}
                                  </span>
                                  {connection.website && (
                                    <span className="flex items-center">
                                      <Globe className="h-3 w-3 mr-1" />
                                      <a 
                                        href={connection.website} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-electric-steel hover:underline"
                                      >
                                        Website
                                      </a>
                                    </span>
                                  )}
                                </div>
                                
                                <p className="text-sm text-warm-slate">{connection.description}</p>
                                
                                {connection.status === 'connected' && (
                                  <div className="flex items-center space-x-6 text-sm">
                                    <span className="text-charcoal-ink">
                                      <Award className="h-3 w-3 inline mr-1" />
                                      {connection.credentialsShared} credentials shared
                                    </span>
                                    <span className="text-charcoal-ink">
                                      <Shield className="h-3 w-3 inline mr-1" />
                                      {connection.verificationsPerformed} verifications
                                    </span>
                                    <span className="text-warm-slate">
                                      Last activity: {new Date(connection.lastActivity).toLocaleDateString()}
                                    </span>
                                  </div>
                                )}

                                {connection.status === 'pending_approval' && (
                                  <div className="flex items-center space-x-2 mt-3">
                                    <Button size="sm" className="credential-capital-button-primary">
                                      <CheckCircle className="h-4 w-4 mr-2" />
                                      Approve
                                    </Button>
                                    <Button size="sm" variant="outline">
                                      <X className="h-4 w-4 mr-2" />
                                      Decline
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Button variant="outline" size="sm">
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </Button>
                            {connection.status === 'connected' && (
                              <Button variant="outline" size="sm">
                                <Settings className="h-4 w-4 mr-2" />
                                Manage
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Trust Network Overview */}
      <Card className="credential-capital-card">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Link className="h-5 w-5 mr-2 text-electric-steel" />
            Trust Network Overview
          </CardTitle>
          <CardDescription>
            Your organization's position in the credential verification network
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-verified-green/10 mb-3">
                <Shield className="h-8 w-8 text-verified-green" />
              </div>
              <h4 className="font-semibold text-charcoal-ink">Direct Connections</h4>
              <p className="text-2xl font-bold text-verified-green mb-2">{connectedCount}</p>
              <p className="text-sm text-warm-slate">Organizations you directly trust</p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-electric-steel/10 mb-3">
                <Users className="h-8 w-8 text-electric-steel" />
              </div>
              <h4 className="font-semibold text-charcoal-ink">Network Reach</h4>
              <p className="text-2xl font-bold text-electric-steel mb-2">47</p>
              <p className="text-sm text-warm-slate">Organizations in extended network</p>
            </div>
            
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/10 mb-3">
                <Award className="h-8 w-8 text-amber-500" />
              </div>
              <h4 className="font-semibold text-charcoal-ink">Trust Score</h4>
              <p className="text-2xl font-bold text-amber-500 mb-2">8.7/10</p>
              <p className="text-sm text-warm-slate">Based on network connections</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}